import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { motion } from "framer-motion";
import {
  Bot, Crown, FileSignature, Landmark, TrendingUp, ShieldCheck, Briefcase, Receipt,
  Play, Check, X, Loader2, AlertTriangle, Inbox, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Crown, FileSignature, Landmark, TrendingUp, ShieldCheck, Briefcase, Receipt, Bot };

const SEV_COLOR = { high: "#EF4444", medium: "#F59E0B", low: "#94A3B8" };

const ACTION_LABEL = {
  create_task: "Create task",
  pin_memory: "Pin memory",
  log_communication: "Log communication",
  draft_briefing: "Draft briefing",
};

export default function Agents() {
  const [agents, setAgents] = useState([]);
  const [actions, setActions] = useState([]);
  const [counts, setCounts] = useState({});
  const [runningId, setRunningId] = useState(null);
  const [busyAction, setBusyAction] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = () => {
    const url = filter ? `/agents-auto/actions?status=${filter}` : "/agents-auto/actions";
    Promise.all([api.get("/agents-auto/agents"), api.get(url)]).then(([a, ac]) => {
      setAgents(a.data);
      setActions(ac.data.actions || []);
      setCounts(ac.data.counts || {});
    });
  };

  useEffect(load, [filter]);

  const runAgent = async (id) => {
    setRunningId(id);
    try {
      const r = await api.post(`/agents-auto/run/${id}`);
      toast.success(`Agent dispatched · ${r.data.proposed_actions.length} actions proposed`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunningId(null);
    }
  };

  const approve = async (aid) => {
    setBusyAction(aid);
    try {
      const r = await api.post(`/agents-auto/actions/${aid}/approve`);
      toast.success(r.data.result ? `Executed: ${r.data.result.type}` : "Approved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusyAction(null);
    }
  };

  const dismiss = async (aid) => {
    setBusyAction(aid);
    try {
      await api.post(`/agents-auto/actions/${aid}/dismiss`);
      load();
    } catch (e) {
      toast.error("Dismiss failed");
    } finally {
      setBusyAction(null);
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="agents-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Bot className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
          <span className="label-eyebrow">// Autonomous Agents</span>
        </div>
        <h1 className="font-display text-3xl text-white tracking-tight">Agent Operations</h1>
        <p className="text-slate-400 text-sm mt-1 max-w-2xl">
          Proactive AI agents scan your institutional data, flag what matters, and post approval-gated actions to your
          inbox. Approve → it executes. Dismiss → nothing happens.
        </p>
      </div>

      {/* Agent fleet */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="agent-fleet">
        {agents.map((a) => {
          const Icon = ICONS[a.icon] || Bot;
          const running = runningId === a.id;
          return (
            <motion.div key={a.id} initial={{ opacity: 0, y: 4 }} animate={{ opacity: 1, y: 0 }} className="nexus-card p-4" data-testid={`agent-${a.id}`}>
              <div className="flex items-start justify-between mb-2">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: "rgba(212,175,55,0.12)", border: "1px solid rgba(212,175,55,0.30)" }}>
                  <Icon className="w-4 h-4" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
                </div>
                {a.last_run_action_count > 0 && (
                  <span className="font-mono text-[10px] text-[#D4AF37] tracking-widest">{a.last_run_action_count} ACT</span>
                )}
              </div>
              <h3 className="font-display text-sm text-white mt-1">{a.name}</h3>
              <p className="text-[11px] text-slate-500 mt-1 mb-3 leading-snug line-clamp-2">{a.tagline}</p>
              {a.last_run_findings?.length > 0 && (
                <div className="mb-3 space-y-1">
                  {a.last_run_findings.slice(0, 2).map((f, i) => (
                    <div key={i} className="text-[11px] text-slate-300 flex items-start gap-1.5 leading-snug">
                      <span className="w-1 h-1 mt-1.5 rounded-full shrink-0" style={{ background: SEV_COLOR[f.severity] || "#94A3B8" }} />
                      <span>{f.title}</span>
                    </div>
                  ))}
                </div>
              )}
              <button
                onClick={() => runAgent(a.id)}
                disabled={running}
                data-testid={`run-${a.id}`}
                className="w-full text-[11px] font-mono tracking-widest py-1.5 flex items-center justify-center gap-1.5 disabled:opacity-60 transition-all"
                style={{ background: "var(--gold-500)", color: "#050B14" }}
              >
                {running ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" strokeWidth={2.2} />}
                {running ? "SCANNING…" : "DISPATCH"}
              </button>
            </motion.div>
          );
        })}
      </div>

      {/* Approvals inbox */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Inbox className="w-3.5 h-3.5" style={{ color: "var(--gold-400)" }} strokeWidth={1.6} />
            <span className="label-eyebrow">// Approvals Inbox</span>
          </div>
          <div className="flex gap-1">
            {["pending", "approved", "dismissed"].map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`filter-${s}`}
                className={`px-3 py-1 font-mono text-[10px] tracking-widest border transition-all ${filter === s ? "border-[#D4AF37]/60 bg-[#D4AF37]/8 text-white" : "border-white/10 text-slate-400 hover:border-white/25"}`}
              >
                {s.toUpperCase()} <span className="text-slate-500">{counts[s] || 0}</span>
              </button>
            ))}
          </div>
        </div>

        {actions.length === 0 ? (
          <div className="nexus-glass-panel p-10 text-center" data-testid="inbox-empty">
            <Sparkles className="w-8 h-8 mx-auto text-slate-600 mb-3" strokeWidth={1.5} />
            <h3 className="font-display text-lg text-white">No {filter} actions</h3>
            <p className="text-sm text-slate-400 mt-1">
              {filter === "pending" ? "Dispatch an agent above to surface suggested actions." : "Nothing here yet."}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="action-inbox">
            {actions.map((a) => (
              <motion.div
                key={a.id}
                initial={{ opacity: 0, x: -4 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-start gap-3 p-4 nexus-card"
                data-testid={`action-${a.id}`}
              >
                <div className="w-1 self-stretch shrink-0" style={{ background: "var(--gold-500)" }} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="label-eyebrow">{a.agent_name}</span>
                    <span className="font-mono text-[9px] text-slate-500 tracking-widest">· {ACTION_LABEL[a.action_type] || a.action_type}</span>
                    {a.status !== "pending" && (
                      <span className={`font-mono text-[9px] tracking-widest ml-auto ${a.status === "approved" ? "text-emerald-400" : "text-slate-500"}`}>
                        {a.status.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-100 leading-snug">{a.description}</p>
                  {a.execution_result && (
                    <div className="text-[11px] text-emerald-400 mt-1">
                      ✓ Created {a.execution_result.type}: {a.execution_result.artifact?.title || a.execution_result.artifact?.id}
                    </div>
                  )}
                </div>
                {a.status === "pending" && (
                  <div className="flex gap-1 shrink-0">
                    <button
                      onClick={() => approve(a.id)}
                      disabled={busyAction === a.id}
                      data-testid={`approve-${a.id}`}
                      className="p-2 border border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10 disabled:opacity-50"
                      title="Approve & execute"
                    >
                      {busyAction === a.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                    </button>
                    <button
                      onClick={() => dismiss(a.id)}
                      disabled={busyAction === a.id}
                      data-testid={`dismiss-${a.id}`}
                      className="p-2 border border-white/10 text-slate-400 hover:border-red-500/40 hover:text-red-400 disabled:opacity-50"
                      title="Dismiss"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
